import React, { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import './MarketingOverviewStyle.css'
import CampaignOverview from './CampaignOverview/CampaignOverview.component'
import MessageOverview from './MessageOverview/MessageOverview.component'
import CampaignType from './CampaignType/CampaignType.component'
import InOutMessage from './InOutMessage/InOutMessage.component'
import { fetchAllCampaigns } from '@store/actions/marketing.action'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { EmptyState } from '@components/ui'
import type { MarketingOutletContextValue } from '@pages/Marketing/marketingOutletContext'
import {
  CampaignMessageStatus,
  ICampaignMessage,
  ICampaignResponse,
  marketingOverviewFilter,
} from '@dataTypes/marketing.type'
import { getDateRange, isDateInRange } from '@utils/date.utils'
/**
 * Overview tab: one period (1D / 1W / …) + Refresh live in Campaign Overview’s header
 * but state lives here so Campaign metrics and every Analytics chart stay in sync.
 * Refresh dispatches fetchAllCampaigns; Redux campaign updates re-run chart hooks.
 * Marketing → Campaign (list) uses CampaignOverview without these props — its own filter.
 */

const isMessageInCurrentPeriod = (
  message: ICampaignMessage,
  startCurrent: Date,
  endCurrent: Date,
): boolean => {
  if (
    message.status === CampaignMessageStatus.sent &&
    message.message_type === 'Outgoing' &&
    message.date_time_sent &&
    isDateInRange(message.date_time_sent, startCurrent, endCurrent)
  ) {
    return true
  }

  if (
    message.status === CampaignMessageStatus.failed &&
    message.message_type === 'Outgoing' &&
    message.date_time_updated_at &&
    isDateInRange(message.date_time_updated_at, startCurrent, endCurrent)
  ) {
    return true
  }

  if (
    message.status === CampaignMessageStatus.pending &&
    message.message_type === 'Outgoing' &&
    message.schedule_date_time
  ) {
    const schedule =
      typeof message.schedule_date_time === 'string'
        ? message.schedule_date_time
        : (message.schedule_date_time as Date).toLocaleString()
    if (isDateInRange(schedule, startCurrent, endCurrent)) {
      return true
    }
  }

  if (
    message.message_type === 'Incoming' &&
    message.date_time_updated_at &&
    isDateInRange(message.date_time_updated_at, startCurrent, endCurrent)
  ) {
    return true
  }

  return false
}

const hasCurrentPeriodMetrics = (
  campaigns: ICampaignResponse[],
  dateFilter: marketingOverviewFilter,
  selectedDate: string | Date,
): boolean => {
  const { startCurrent, endCurrent } = getDateRange(
    dateFilter,
    new Date(selectedDate),
  )

  return campaigns.some((campaign) =>
    (campaign.campaign_message ?? []).some((message) =>
      isMessageInCurrentPeriod(message, startCurrent, endCurrent),
    ),
  )
}

const MarketingOverview: React.FC = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { openAddCampaignModal } =
    useOutletContext<MarketingOutletContextValue>()
  const overviewDateFilter = useAppSelector(
    (state) => state.marketing.overviewDateFilter,
  )
  const selectedDate = useAppSelector((state) => state.marketing.selectedDate)
  const campaigns = useAppSelector((state) => state.marketing.campaigns)
  const clinic = useAppSelector((state) => state.clinic.selectedClinic)

  useEffect(() => {
    dispatch(fetchAllCampaigns())
  }, [clinic, dispatch])

  const hasNoCampaigns = campaigns.length === 0
  const allMetricsZero = !hasCurrentPeriodMetrics(
    campaigns,
    overviewDateFilter,
    selectedDate,
  )
  const showEmptyState = allMetricsZero
  const showCreateAction = hasNoCampaigns

  const emptyDescription = useMemo(() => {
    if (showCreateAction) {
      return "You don't have any campaigns on record yet."
    }
    return 'No campaign activity for this period.'
  }, [showCreateAction])

  return (
    <div
      className={`marketing-overview${
        showEmptyState ? ' marketing-overview--empty' : ''
      }`}
    >
      {showEmptyState ? (
        <EmptyState
          className="marketing-overview-empty"
          iconClass="mgc mgc_announcement_fill"
          title="No marketing data found"
          description={emptyDescription}
          actionLabel={showCreateAction ? 'Create campaign' : undefined}
          onAction={showCreateAction ? openAddCampaignModal : undefined}
        />
      ) : (
        <>
          <CampaignOverview dateFilter={overviewDateFilter} />
          <div className="mo-analytics">
            <span className="mo-header">Analytics</span>
            <MessageOverview overviewDateFilter={overviewDateFilter} />
            <CampaignType overviewDateFilter={overviewDateFilter} />
            <InOutMessage overviewDateFilter={overviewDateFilter} />
          </div>
        </>
      )}
    </div>
  )
}

export default MarketingOverview
